/**
 * A swipe UP with four fingers or more — a whole hand — anywhere in the app opens "Invite a
 * friend" (plan.md step 89). Four since 2026-10-30, everywhere (the user, on Android: "it's hiding
 * the app — maybe we need 4 fingers": many Android phones keep a three-finger swipe for
 * themselves — a screenshot, the recent apps). The user, 2026-10-29, replacing the three two-finger taps of the
 * same morning: "it's working but not good, not all the time and pressed buttons on the screen,
 * maybe let's do a full hand slide from bottom to top? and when it does don't scroll the page."
 * No phone build: the web app hears it, in a browser and in both phone apps.
 *
 * - **Four fingers or more**, moving together from lower on the screen to higher: on average
 *   at least `SWIPE_MIN_PX` up, more up than sideways, within `SWIPE_MAX_MS` of the fourth finger
 *   landing. One to three fingers are left to the page and the phone — scrolls, taps, the map's
 *   pinch, Android's own three-finger gestures.
 * - **It never presses what is under it.** From the moment the fourth finger lands until the last
 *   one lifts, every touch event is cancelled (`preventDefault`): no scroll, no zoom, no click
 *   made from the touches — and a click that arrives anyway within `SWALLOW_CLICK_MS` of the
 *   lift is swallowed before anything below hears it.
 * - **It opens when the hand lifts**, never while the fingers are still on the glass — otherwise
 *   the lifting fingers would land on the screen that just opened.
 *
 * - **Android (the 0.14.76 walk: "not working on Android", web and app).** Three things an Android
 *   phone does that an iPhone does not, each of which threw the gesture away: a `touchcancel` for
 *   ONE finger (palm rejection, a finger the screen lost) reset the whole hand although three
 *   fingers were still sliding; a finger that slips off mid-swipe left three on the glass and every
 *   later move was ignored; and the phone taking the touches for itself (`touchcancel` with nothing
 *   left) after the hand had started up but before `SWIPE_MIN_PX`. Now a cancel of some fingers is a
 *   lift of those fingers; a hand once formed keeps counting on `FINGERS - 1`; the travel is the
 *   average of the fingers that moved most (a finger that re-lands does not drag it down); and a
 *   hand the phone cancels on the way up opens once it had travelled `CANCELLED_MIN_PX`.
 *
 * With VoiceOver or TalkBack on, the phone keeps multi-finger swipes for itself (they scroll) —
 * the menu's "Invite a friend" row is the way in there.
 */
import { useEffect, useRef } from 'react';

/** Fingers needed — a whole hand is four or more (three on Android is the phone's own gesture). */
export const FINGERS = 4;
/** How far up the fingers travel, on average, before it counts. */
export const SWIPE_MIN_PX = 60;
/** …within this of the fourth finger landing. */
export const SWIPE_MAX_MS = 1500;
/** A hand the phone cancels on its way up opens once it had travelled this far (Android; the 0.14.76 walk). */
export const CANCELLED_MIN_PX = 24;
/** A click this soon after a whole-hand gesture lifted came from it, and is swallowed. */
export const SWALLOW_CLICK_MS = 450;

export interface Finger { id: number; x: number; y: number }

/**
 * The recognising, apart from the DOM (so it is tested without one): fed every finger on the
 * screen at each touch event, `end` answers true when the last finger lifts off a whole-hand
 * swipe up. `hand` is true while the gesture is one of four fingers or more — the caller then
 * cancels the event.
 */
export function fullHandSwipe() {
  /** Where each finger of this gesture landed. */
  let from = new Map<number, { x: number; y: number }>();
  let handAt: number | null = null;
  let swiped = false;
  /** The hand has started up the screen — enough to count when the phone cancels the touches. */
  let begun = false;

  const reset = () => { from = new Map(); handAt = null; swiped = false; begun = false; };
  const land = (fingers: Finger[]) => {
    for (const f of fingers) if (!from.has(f.id)) from.set(f.id, { x: f.x, y: f.y });
  };
  /** The fingers left on the glass after a lift or a cancel; true when the last one off ends a swipe. */
  const lift = (remaining: Finger[], cancelled: boolean): boolean => {
    const still = new Set(remaining.map((f) => f.id));
    for (const id of [...from.keys()]) if (!still.has(id)) from.delete(id);
    if (remaining.length > 0) return false;
    const done = handAt !== null && (swiped || (cancelled && begun));
    reset();
    return done;
  };

  return {
    get hand(): boolean { return handAt !== null; },
    /** A whole hand that has already travelled up — kept if the phone cancels the touches before they lift. */
    get swiped(): boolean { return handAt !== null && swiped; },
    /** A finger landed; `fingers` is every finger now on the screen. */
    start(fingers: Finger[], at: number): void {
      if (from.size === 0) reset();
      land(fingers);
      if (fingers.length >= FINGERS && handAt === null) handAt = at;
    },
    move(fingers: Finger[], at: number): void {
      land(fingers);
      // Once the hand is formed, one finger may slip off the glass (Android) and it still counts.
      if (handAt === null || swiped || fingers.length < FINGERS - 1 || at - handAt > SWIPE_MAX_MS) return;
      // The fingers that moved up most — a finger that lifted and landed again starts from where it re-landed.
      const moved = fingers
        .map((f) => { const o = from.get(f.id)!; return { dx: f.x - o.x, dy: f.y - o.y }; })
        .sort((a, b) => a.dy - b.dy)
        .slice(0, FINGERS - 1);
      let dx = 0;
      let dy = 0;
      for (const m of moved) { dx += m.dx; dy += m.dy; }
      dx /= moved.length;
      dy /= moved.length;
      // Up the screen is a smaller y.
      if (Math.abs(dx) >= -dy) return;
      if (-dy >= CANCELLED_MIN_PX) begun = true;
      if (-dy >= SWIPE_MIN_PX) swiped = true;
    },
    /** A finger lifted; `remaining` is every finger still down. True on the lift that ends a swipe. */
    end(remaining: Finger[]): boolean {
      return lift(remaining, false);
    },
    /**
     * The phone cancelled touches (`touchcancel`): with fingers still down, only those fingers are gone (Android
     * cancels one finger at a time — palm rejection); with none left, a hand already on its way up still opens.
     */
    cancelled(remaining: Finger[]): boolean {
      return lift(remaining, true);
    },
    cancel(): void { reset(); },
  };
}

const fingersOf = (list: TouchList): Finger[] => Array.from(list, (t) => ({ id: t.identifier, x: t.clientX, y: t.clientY }));

/** Until when a click is a whole hand's leftover — one guard for the window, put on once. */
let swallowUntil = 0;
let guarded = false;
function swallowClicks(now: number): void {
  swallowUntil = now + SWALLOW_CLICK_MS;
  if (guarded || typeof window === 'undefined') return;
  guarded = true;
  window.addEventListener('click', (e) => {
    if (Date.now() >= swallowUntil) return;
    e.preventDefault();
    e.stopPropagation();
  }, { capture: true });
}

/** Listen on the whole window while `on`; `onSwipe` when a whole hand swipes up and lifts. */
export function useFullHandSwipe(on: boolean, onSwipe: () => void): void {
  const fire = useRef(onSwipe);
  fire.current = onSwipe;
  useEffect(() => {
    if (!on || typeof window === 'undefined') return undefined;
    const g = fullHandSwipe();
    // While a whole hand is down nothing of the page moves or is pressed.
    // From the THIRD finger too (the 0.14.73 walk, Android: "not working"): a page that starts scrolling under the
    // first fingers lets Android cancel the touches before the fourth lands. Two-finger pinch is never held.
    const hold = (e: TouchEvent) => { if ((g.hand || e.touches.length >= FINGERS - 1) && e.cancelable) e.preventDefault(); };
    const start = (e: TouchEvent) => { g.start(fingersOf(e.touches), e.timeStamp); hold(e); };
    const move = (e: TouchEvent) => { g.move(fingersOf(e.touches), e.timeStamp); hold(e); };
    const end = (e: TouchEvent) => {
      const hand = g.hand;
      hold(e);
      const swiped = g.end(fingersOf(e.touches));
      if (hand) swallowClicks(Date.now());
      if (swiped) fire.current();
    };
    // The phone took touches for itself. Some of them (Android's palm rejection): those fingers are lifted, the hand
    // goes on. All of them: a whole hand that was already on its way up still opens (the 0.14.73 and 0.14.76 walks).
    const cancel = (e: TouchEvent) => {
      const hand = g.hand;
      const done = g.cancelled(fingersOf(e.touches));
      if (hand) swallowClicks(Date.now());
      if (done) fire.current();
    };
    // In the capture phase, so it is heard before anything below can stop it; NOT passive,
    // because only a cancellable listener can keep the page from scrolling under the hand.
    // Fewer than four fingers return at once and are never cancelled.
    const opts = { capture: true, passive: false } as const;
    window.addEventListener('touchstart', start, opts);
    window.addEventListener('touchmove', move, opts);
    window.addEventListener('touchend', end, opts);
    window.addEventListener('touchcancel', cancel, opts);
    return () => {
      window.removeEventListener('touchstart', start, opts);
      window.removeEventListener('touchmove', move, opts);
      window.removeEventListener('touchend', end, opts);
      window.removeEventListener('touchcancel', cancel, opts);
    };
  }, [on]);
}
